import { motion } from "framer-motion";

/**
 * Iklan berjalan — auto-scrolling horizontal banner of image ads.
 * Each ad may have a hyperlink; when set, the image becomes clickable.
 * If no admin has uploaded any ad yet, this component renders nothing
 * so the landing page keeps its clean flow.
 */
export default function Iklan({ gallery }) {
  const items = (gallery || []).filter((it) => it && it.image && String(it.image).trim().length > 0);
  if (items.length === 0) return null;

  // Duplicate the list to create a seamless marquee loop.
  const loop = [...items, ...items];

  return (
    <section
      id="iklan-berjalan"
      className="relative bg-forest text-parchment py-10 md:py-12 border-y border-gold/30 overflow-hidden"
      data-testid="iklan-band"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-6 flex items-center justify-between gap-4">
        <div>
          <p className="text-[10px] tracking-[0.45em] uppercase text-gold">Kolaborasi & Pengumuman</p>
          <h3 className="mt-1 font-serif-display text-parchment text-2xl md:text-3xl">
            Iklan &amp; sorotan pondok.
          </h3>
        </div>
        <div className="hidden md:block flex-1 h-px bg-gold/25" />
      </div>

      <div className="relative w-full overflow-hidden">
        {/* soft gradient edges for elegant fade-out */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-24 bg-gradient-to-r from-forest to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-24 bg-gradient-to-l from-forest to-transparent z-10" />

        <motion.div
          className="flex gap-6 md:gap-8 will-change-transform"
          animate={{ x: ["0%", "-50%"] }}
          transition={{
            duration: Math.max(20, items.length * 6),
            ease: "linear",
            repeat: Infinity,
          }}
          data-testid="iklan-track"
        >
          {loop.map((it, i) => {
            const hasLink = it.link && String(it.link).trim().length > 0;
            const inner = (
              <div className="relative w-[260px] md:w-[340px] aspect-[16/9] rounded-2xl overflow-hidden border border-gold/40 bg-sand shadow-lg group">
                <img
                  src={it.image}
                  alt={`Iklan ${((i % items.length) + 1)}`}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                {hasLink && (
                  <div className="absolute inset-0 bg-gradient-to-t from-forest/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end justify-end p-3">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-parchment bg-gold/90 px-3 py-1 rounded-full">
                      Kunjungi →
                    </span>
                  </div>
                )}
              </div>
            );
            return hasLink ? (
              <a
                key={i}
                href={it.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 block"
                data-testid={`iklan-link-${i % items.length}`}
              >
                {inner}
              </a>
            ) : (
              <div key={i} className="flex-shrink-0" data-testid={`iklan-item-${i % items.length}`}>
                {inner}
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
